import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuIcon from '@mui/icons-material/Menu';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShareIcon from '@mui/icons-material/Share';
import './install.styles.css';

/**
 * Instalá CambiApp en tu celular. Es el destino del enlace cambiapp.net/install
 * y del QR: el que llega acá tiene todo lo necesario para instalarla sin que
 * nadie le explique nada.
 *
 * Casos que resuelve, todos vistos en la práctica:
 *  · Navegador interno de WhatsApp/Instagram: ahí no se puede instalar. Es el
 *    motivo número uno por el que la gente cree que "no funciona".
 *  · Android con Chrome: se ofrece "Instalar ahora" (evento del navegador). No
 *    cubre a todos (otro navegador, incógnito, ya instalada), así que los pasos
 *    manuales quedan siempre a la vista.
 *  · Navegador de Samsung: otro camino (menú abajo), se detecta aparte.
 *  · iPhone: solo desde Safari (decisión de Apple). Si entró con Chrome, se avisa.
 *
 * OJO: el nombre de la opción del menú NO es estable (cambia con la versión de
 * Chrome y con el idioma del teléfono), por eso se nombran las variantes.
 */

const URL_APP = 'https://cambiapp.net/install';
const URL_CORTA = 'cambiapp.net/install';
const MENSAJE = 'Instalá CambiApp como app en tu celular, en 3 pasos: https://cambiapp.net/install';

type Aparato = 'android' | 'iphone' | 'pc';
type PromptInstalacion = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

interface Paso {
	texto: React.ReactNode;
	icono?: React.ReactNode;
}

const PASOS: Record<'androidChrome' | 'androidSamsung' | 'iphone' | 'pc', Paso[]> = {
	androidChrome: [
		{ texto: <>Entrá a <b>cambiapp.net</b> en Chrome</> },
		{ texto: <>Tocá el menú de 3 puntitos, arriba a la derecha</>, icono: <MoreVertIcon className="icon" /> },
		{
			texto: (
				<>
					Elegí la opción de instalar. Según tu teléfono dice <b>"Instalar aplicación"</b>,{' '}
					<b>"Instalar y crear acceso directo"</b> o <b>"Agregar a la pantalla principal"</b>. Confirmá
					con <b>Instalar</b> y aceptá los permisos de notificaciones.
				</>
			),
			icono: <InstallMobileIcon className="icon" />,
		},
	],
	androidSamsung: [
		{ texto: <>Entrá a <b>cambiapp.net</b> en el navegador de Samsung</> },
		{ texto: <>Tocá el menú de 3 rayas, abajo a la derecha</>, icono: <MenuIcon className="icon" /> },
		{ texto: <>Elegí <b>"Agregar página a"</b> y después <b>"Pantalla de inicio"</b></> },
	],
	iphone: [
		{
			texto: (
				<>
					Entrá a <b>cambiapp.net</b> en <b>Safari</b> (en iPhone solo funciona desde ahí, no desde Chrome ni
					desde el enlace de WhatsApp)
				</>
			),
		},
		{
			texto: <>Tocá el botón <b>Compartir</b>, abajo en la barra de Safari</>,
			icono: <IosShareIcon className="icon" />,
		},
		{
			texto: (
				<>
					Deslizá hacia abajo en la lista y elegí <b>"Agregar a pantalla de inicio"</b> (en algunos
					teléfonos dice "Añadir…")
				</>
			),
			icono: <AddBoxOutlinedIcon className="icon" />,
		},
		{ texto: <>Tocá <b>Agregar</b>, arriba a la derecha, para confirmar</> },
	],
	pc: [
		{ texto: <>Entrá a <b>cambiapp.net</b> en Chrome o Edge</> },
		{
			texto: <>Hacé clic en el ícono de instalar que aparece a la derecha de la barra de direcciones</>,
			icono: <InstallDesktopIcon className="icon" />,
		},
		{ texto: <>Confirmá con <b>Instalar</b>. Queda en el escritorio y en el menú de inicio.</> },
	],
};

const InstallPage = () => {
	const navigate = useNavigate();
	const [aparato, setAparato] = useState<Aparato>('pc');
	const [samsung, setSamsung] = useState(false);
	const [iosSinSafari, setIosSinSafari] = useState(false);
	const [enApp, setEnApp] = useState(false);
	const [yaInstalada, setYaInstalada] = useState(false);
	const [prompt, setPrompt] = useState<PromptInstalacion | null>(null);
	const [copiado, setCopiado] = useState(false);
	const [puedeCompartir, setPuedeCompartir] = useState(false);

	useEffect(() => {
		const ua = navigator.userAgent || '';
		const esIphone = /iPad|iPhone|iPod/.test(ua);
		const esAndroid = /Android/i.test(ua);
		setAparato(esIphone ? 'iphone' : esAndroid ? 'android' : 'pc');
		setSamsung(/SamsungBrowser/i.test(ua));
		// En iPhone, Chrome/Firefox/Edge son Safari por dentro pero NO pueden instalar.
		setIosSinSafari(esIphone && /(CriOS|FxiOS|EdgiOS|OPiOS)/i.test(ua));
		setEnApp(/(WhatsApp|Instagram|FBAN|FBAV|FB_IAB|Line\/|Snapchat)/i.test(ua));
		setYaInstalada(window.matchMedia('(display-mode: standalone)').matches);
		setPuedeCompartir(typeof navigator.share === 'function');

		const alPromptear = (e: Event) => {
			e.preventDefault();
			setPrompt(e as PromptInstalacion);
		};
		window.addEventListener('beforeinstallprompt', alPromptear);
		return () => window.removeEventListener('beforeinstallprompt', alPromptear);
	}, []);

	const instalar = async () => {
		if (!prompt) return;
		await prompt.prompt();
		await prompt.userChoice.catch(() => null);
		setPrompt(null);
	};

	const copiar = async () => {
		try {
			await navigator.clipboard.writeText(URL_APP);
			setCopiado(true);
			setTimeout(() => setCopiado(false), 2500);
		} catch {
			/* la dirección está escrita a la vista igual */
		}
	};

	const compartir = async () => {
		try {
			await navigator.share({ title: 'CambiApp', text: MENSAJE, url: URL_APP });
		} catch {
			/* canceló */
		}
	};

	const lista = aparato === 'iphone' ? PASOS.iphone : aparato === 'android' ? (samsung ? PASOS.androidSamsung : PASOS.androidChrome) : PASOS.pc;
	const titulo =
		aparato === 'iphone' ? 'En tu iPhone' : aparato === 'android' ? (samsung ? 'En tu Android · navegador de Samsung' : 'En tu Android') : 'En la computadora';

	const Pasos = ({ pasos }: { pasos: Paso[] }) => (
		<ol className="pasos">
			{pasos.map((p, i) => (
				<li key={i}>
					<span className="numero">{i + 1}</span>
					<span className="texto">
						{p.texto}
						{p.icono && <span className="icono-pista"> {p.icono}</span>}
					</span>
				</li>
			))}
		</ol>
	);

	return (
		<Box className="install-page">
			<Box className="main-container">
				<Button onClick={() => navigate('/')} className="volver">
					{'< Volver'}
				</Button>

				<Box className="cabecera">
					<img src="/cambiapp_logo.png" alt="CambiApp" className="logo" />
					<Typography variant="h5" component="h1" className="titulo">
						Instalá CambiApp como una app en tu celu
					</Typography>
					<Typography className="bajada">Sin descargar nada de la tienda. Es un minuto.</Typography>
					<Box className="icono-app">
						<img src="/logo192.png" alt="Ícono de CambiApp" />
						<span>Va a quedar en tu pantalla con este ícono</span>
					</Box>
				</Box>

				{yaInstalada && (
					<Box className="tarjeta ok">
						<b>✓ Ya la tenés instalada</b>
						<span>Estás usando CambiApp como aplicación. Buscá el ícono en la pantalla de tu celular.</span>
					</Box>
				)}

				{/* Navegador interno de WhatsApp: desde ahí NO se puede instalar. */}
				{enApp && !yaInstalada && (
					<Box className="tarjeta aviso">
						<b>⚠ Abrilo en tu navegador para poder instalarlo</b>
						<span>
							Estás viendo esta página <b>dentro de WhatsApp</b>, y desde acá el celular no deja instalar. Tocá
							el menú (⋮ o ···) arriba a la derecha y elegí <b>"Abrir en el navegador"</b>. O copiá la dirección
							y pegala en {aparato === 'iphone' ? 'Safari' : 'Chrome'}.
						</span>
						<Button onClick={copiar} startIcon={<ContentCopyIcon />} className="boton-claro">
							{copiado ? '✓ Dirección copiada' : `Copiar ${URL_CORTA}`}
						</Button>
					</Box>
				)}

				{/* iPhone con Chrome/Firefox: Apple solo permite instalar desde Safari. */}
				{iosSinSafari && !enApp && !yaInstalada && (
					<Box className="tarjeta aviso">
						<b>⚠ En iPhone hay que usar Safari</b>
						<span>
							Estás usando otro navegador, y Apple solo permite instalar desde <b>Safari</b>. Copiá la dirección
							y abrila en Safari para seguir.
						</span>
						<Button onClick={copiar} startIcon={<ContentCopyIcon />} className="boton-claro">
							{copiado ? '✓ Dirección copiada' : `Copiar ${URL_CORTA}`}
						</Button>
					</Box>
				)}

				{/* Camino principal en Android: instalar de un toque. */}
				{prompt && !enApp && !yaInstalada && (
					<Box className="tarjeta instalar">
						<b>Tu celular permite instalarla directamente</b>
						<Button onClick={instalar} variant="contained" className="boton-instalar" startIcon={<InstallMobileIcon />}>
							Instalar ahora
						</Button>
						<span>No descarga nada de la tienda: son unos segundos.</span>
					</Box>
				)}

				{!yaInstalada && (
					<Box className="seccion">
						<Typography className="subtitulo">
							{prompt ? 'O a mano, en pocos pasos' : titulo}
						</Typography>
						<Pasos pasos={lista} />
						{aparato === 'android' && !samsung && (
							<Typography className="nota">
								¿Usás el navegador de Samsung? Tocá el menú de 3 rayas abajo a la derecha, elegí "Agregar página
								a" y después "Pantalla de inicio".
							</Typography>
						)}
						{aparato === 'pc' && (
							<Box className="otros">
								<Typography className="subtitulo">En el celular</Typography>
								<Typography className="nota">
									<b>Android:</b> menú ⋮ de Chrome → "Instalar aplicación" (o "Agregar a la pantalla principal").{' '}
									<b>iPhone:</b> en Safari, botón Compartir → "Agregar a pantalla de inicio" → Agregar.
								</Typography>
							</Box>
						)}
					</Box>
				)}

				<Box className="seccion qr">
					<Typography className="subtitulo">¿Estás en la computadora?</Typography>
					<Typography className="nota">Escaneá este código con la cámara de tu celular y seguí ahí.</Typography>
					<img src="/qr-install.png" alt="Código QR de cambiapp.net/install" className="qr-img" />
					<Typography className="direccion">{URL_CORTA}</Typography>
				</Box>

				<Box className="seccion">
					<Typography className="subtitulo">Pasásela a otra persona</Typography>
					<Typography className="nota">
						Mandale el enlace de instalación, o mostrale el código para que lo escanee de tu pantalla.
					</Typography>
					<Box className="acciones">
						<Button
							component="a"
							href={`https://wa.me/?text=${encodeURIComponent(MENSAJE)}`}
							target="_blank"
							rel="noopener noreferrer"
							startIcon={<WhatsAppIcon />}
							className="boton-wa"
						>
							Enviar por WhatsApp
						</Button>
						{puedeCompartir && (
							<Button onClick={compartir} startIcon={<ShareIcon />} className="boton-claro">
								Compartir
							</Button>
						)}
						<Button onClick={copiar} startIcon={<ContentCopyIcon />} className="boton-claro">
							{copiado ? '✓ Copiado' : 'Copiar enlace'}
						</Button>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default InstallPage;
